import defaultTestObject from './defaultGPUTests.js';

let testObject;
if (typeof __GPU_TESTS__ !== 'undefined') {
    testObject = __GPU_TESTS__;
} else {
    testObject = defaultTestObject;
}

const UNKNOWN = 'unknown';
const match = (str, a, all) => a.reduce(
    (p, c) => all ? (p && !!str.match(c)) : (p || !!str.match(c)),
    false
);
const findType = (str, o) => { for (const k in o) if (match(str, o[k])) return k; };

const BROWSERS = {
    edge: [ 'edge', 'edg' ],
    chrome: [ 'chrome', 'crios' ],
    firefox: [ 'firefox', 'fxios' ],
    ie: [ 'msie', 'trident', 'rv:' ],
    ucbrowser: [ 'ucbrowser' ],
    safari: [ 'safari', 'ios' ],
    opera: [ 'opera', 'opios' ]
};

const QUALITIES = {
    0: 'verylow',
    1: 'low',
    2: 'medium',
    3: 'high',
    4: 'veryhigh',
    5: 'ultra'
};


export default function getDeviceInfos(userAgent) {
    const infos = {};

    infos.userAgent = typeof userAgent === 'string'
        ? userAgent
        : navigator.userAgent.toLowerCase();

    infos.hasTouch = hasTouch();
    infos.type = getDeviceType(infos);
    infos.os = getOS(infos);
    infos.browser = getBrowser(infos);
    infos.browserVersion = getBrowserVersion(infos);

    let gl = webglCreateContext(infos, true);
    const failed = !gl;
    webglDestroyContext(gl);

    gl = webglCreateContext(infos, false);
    infos.majorPerformanceCaveat = (gl && failed);
    infos.webgl = gl ? getWebGL(infos, gl) : null;
    webglDestroyContext(gl);
    gl = null;

    infos.gpu = gpuGetQualityObject('low');
    infos.gpuDetectionFinished = infos.webgl
        ? getGPU(infos).then(gpu => Object.assign(infos.gpu, gpu))
        : Promise.resolve(infos.gpu);

    return infos;
}


function hasTouch() {
    return ('ontouchstart' in window) || (navigator.maxTouchPoints > 1);
}


function isiOS(userAgent, hasTouch) {
    const isApple = match(userAgent, [ 'ipad', 'mac', 'macos' ]);
    const isIphone = match(userAgent, [ 'iphone' ]);
    const isMac = navigator.platform === 'MacIntel';
    return !!(!isIphone && isApple && isMac && hasTouch);
}


function getDeviceType({ hasTouch, userAgent }) {
    const mobiles = [ 'ios', 'iphone', 'ipad', 'phone', 'android', 'blackberry' ];
    const hasTabletScreen = Math.max(screen.width, screen.height) > 1020;
    const fakeAndroidSafari = match(userAgent, [ 'android', 'safari' ], true);
    const forceTablet = isiOS(userAgent, hasTouch) || fakeAndroidSafari;
    const mobile = !!(forceTablet || hasTouch && match(userAgent, mobiles));
    const tablet = !!(mobile && (forceTablet || hasTabletScreen));
    const desktop = !mobile;
    const phone = !desktop && !(tablet && forceTablet);
    return { desktop, mobile, tablet, phone };
}


function getOS({ hasTouch, type, userAgent }) {
    if (isiOS(userAgent, hasTouch)) return 'ios';
    const list = {
        desktop: { windows: [ 'windows', 'iemobile' ], linux: [ 'linux' ], macos: [ 'mac os' ] },
        mobile: { android: [ 'android' ], ios: [ 'ipad', 'iphone' ], blackberry: [ 'blackberry' ] }
    };
    const osList = type.desktop ? list.desktop : list.mobile;
    return findType(userAgent, osList) || UNKNOWN;
}


function getBrowser({ userAgent, os }) {
    const browser = findType(userAgent, BROWSERS);
    if (browser) return browser;
    if (os === 'ios') return 'safari';
    return UNKNOWN;
}


function getBrowserVersion({ userAgent, browser }) {
    const parse = name => {
        const splitted = userAgent.split(name)[1];
        if (!splitted || splitted.length <= 0) return;
        const value = parseFloat(
            splitted.split(' ')[0].split('.')[0].replace(/[^.0-9]/g, '')
        );
        if (isNaN(value)) return;
        return value;
    };
    switch (browser) {
        case 'chrome':
        case 'firefox':
            let found;
            BROWSERS[browser].forEach(id => { const p = parse(id); if (p != null) found = p; });
            return found;
        case 'safari': {
            let v = userAgent.match(/version\/([.\d]+)/i);
            if (v && v[1]) return parseFloat(v[1]);
            v = userAgent.match(/os ([0-9_]+)/i);
            if (v && v[1]) return parseFloat(v[1].split('_')[0]);
            return;
        }
        case 'ie':
        case 'edge':
            if (match(userAgent, [ 'msie' ])) return parse('msie');
            if (match(userAgent, [ 'rv:' ])) return parse('rv:');
            return parse('edge/');
        default:
            return;
    }
}


async function getGPU({ os, webgl }) {
    const gpu = {
        string: null,
        quality: { low: true },
        qualityIndex: 1,
        type: null,
        series: null,
        version: null,
        numbers: [],
        isMobile: null
    };

    const gpuTypes = {
        intel: [ 'intel' ],
        nvidia: [ 'nvidia', 'geforce' ],
        amd: [ 'amd', 'radeon' ],
        adreno: [ 'adreno' ],
        apple: [ 'apple' ],
        mali: [ 'mali' ],
        swiftshader: [ 'swiftshader' ]
    };

    const infos = webgl && webgl.rendererInfos;
    const unmaskedRenderer = (webgl && webgl.rendererUnmasked) || '';
    if (!infos || !unmaskedRenderer.length) return gpu;

    gpu.string = gpuCleanRendererString(unmaskedRenderer);
    gpu.type = findType(gpu.string, gpuTypes) || UNKNOWN;
    if (gpu.type === UNKNOWN) return gpu;

    if (os === 'ios' && gpu.string === 'apple gpu') {
        gpu.string = 'apple gpu';
        gpu.string = gpuCleanRendererString(gpu.string);
    }

    gpu.isMobile = gpu.string[gpu.string.length - 1] === 'm';
    Object.assign(gpu, gpuParseVersion(gpu.string));
    gpu.series = gpuGetSeries(gpu.string);
    Object.assign(gpu, gpuEstimateQuality(infos, gpu));
    return gpu;
}


function getWebGL(infos, gl) {
    const vendorPrefixes = [ 'WEBKIT_', 'MOZ_' ];
    let exts = gl.getSupportedExtensions() || [];
    exts = exts.reduce((p, v) => {
        for (let i = 0; i < vendorPrefixes.length; i++) {
            if (!v.indexOf(vendorPrefixes[i]))
                return p[v.substring(vendorPrefixes[i].length)] = v, p;
        }
        return p[v] = v, p;
    }, {});

    const prefix = 'WEBGL_compressed_texture_';
    const formats = [ 's3tc', 'astc', 'etc', 'pvrtc' ]
        .reduce((p, v) => (p[v] = !!exts[prefix + v], p), {});

    const supported = infos.browser !== 'firefox';
    const rendererDef = gl.getParameter(gl.RENDERER);
    const rendererInfos = supported && gl.getExtension(exts.WEBGL_debug_renderer_info);
    const rendererUnmasked = rendererInfos
        ? gl.getParameter(rendererInfos.UNMASKED_RENDERER_WEBGL)
        : rendererDef;

    return {
        renderer: (gl.getParameter(gl.RENDERER) || '').toLowerCase(),
        rendererInfos,
        rendererUnmasked,
        version: (gl.getParameter(gl.VERSION) || '').toLowerCase(),
        glsl: (gl.getParameter(gl.SHADING_LANGUAGE_VERSION) || '').toLowerCase(),
        extensions: exts,
        compressedTextures: formats
    };
}


function webglCreateContext(infos, failIfMajorPerformanceCaveat) {
    const canvas = document.createElement('canvas');
    let gl;
    try {
        const opts = {
            alpha: false, antialias: false, depth: false,
            failIfMajorPerformanceCaveat,
            powerPreference: 'high-performance',
            stencil: false
        };
        if (infos.browser === 'safari' && infos.browserVersion === 12 && infos.type.desktop) {
            delete opts.powerPreference;
        }
        gl = canvas.getContext('webgl', opts)
            || canvas.getContext('webgl-experimental', opts)
            || canvas.getContext('experimental-webgl', opts);
    } catch(e) { /* */ }
    return gl;
}


function webglDestroyContext(gl) {
    if (!gl || !gl.getExtension('WEBGL_lose_context')) return;
    gl.getExtension('WEBGL_lose_context').loseContext();
}


function gpuParseVersion(renderer) {
    const splitted = renderer.split(' ');
    const numbers = splitted
        .map(v => v.replace(/[\D]/g, ''))
        .filter(w => w.length > 0)
        .map(v => parseFloat(v));

    const version = numbers[0] || null;

    if (renderer.startsWith('apple a')) {
        const letter = splitted[1] && splitted[1][splitted[1].length - 1];
        if (letter === 'z') numbers[1] = 100;
        else if (letter === 'x') numbers[1] = 10;
        else numbers[1] = 1;
    }
    return { numbers, version };
}


function gpuGetSeries(renderer) {
    const seriesList = {
        'swiftshader': 'swiftshader',
        'apple a': 'apple a', 'apple m2': 'apple m2', 'apple m1': 'apple m1',
        'apple m': 'apple m', 'apple gpu': 'apple gpu',
        'geforce gtx': 'geforce gtx', 'geforce rtx': 'geforce rtx',
        'geforce mx': 'geforce mx', 'titan': 'geforce titan',
        'quadro fx': 'quadro fx', 'quadro p': 'quadro p',
        'quadro rtx': 'quadro rtx', 'quadro ': 'quadro',
        'geforce ': 'geforce', 'tegra ': 'tegra',
        'radeon vii': 'radeon vii', 'radeon r7': 'radeon r7',
        'radeon r9': 'radeon r9', 'radeon r10': 'radeon r10',
        'radeon rx': 'radeon rx', 'radeon pro vega': 'radeon pro vega',
        'radeon rx vega': 'radeon rx vega', 'radeon hd': 'radeon hd',
        'radeon pro ': 'radeon pro', 'radeon ': 'radeon',
        'intel iris plus ': 'intel iris plus', 'intel iris pro ': 'intel iris pro',
        'intel iris ': 'intel iris', 'intel hd ': 'intel hd', 'intel uhd ': 'intel uhd',
        'adreno': 'adreno',
        'mali-t': 'mali t', 'mali-g': 'mali g', 'mali': 'mali'
    };
    const series = {};
    for (const k in seriesList) { if (renderer.indexOf(k) > -1) series[seriesList[k]] = true; }
    return series;
}


function gpuCleanRendererString(rendererString) {
    let s = rendererString.toLowerCase().replace(/(\(tm\)|\(r\))/g, '').trim();
    if (s.includes('angle (') && s.includes('direct3d')) {
        s = s.replace('angle (', '').split(' direct3d')[0];
    }
    if (s.includes('nvidia') && s.includes('gb')) {
        s = s.split(/\dgb/)[0];
    }
    return s;
}


function gpuEstimateQuality({ type, browser }, gpu) {
    if (!gpu || !gpu.type || gpu.type === 'swiftshader') {
        if (browser === 'firefox' && type.desktop) return gpuGetQualityObject('medium');
        return gpuGetQualityObject('low');
    }

    const mobileGPU = gpu.isMobile;
    const gpuBrand = gpu.type;
    const gpuSeries = gpu.series;
    const gpuVersion = gpu.version || 0;
    let gpuSubNumber = gpu.numbers[1] || 1;

    const maliMatches = gpu.string.match(/(?:^| )mp(\d)+(?: |$)/i);
    if (maliMatches) gpuSubNumber = parseFloat(maliMatches[1]);

    const is = (serie) => gpuSeries[serie];
    const le = (serie, version, subNumber) => is(serie) && version <= gpuVersion
        && (subNumber === undefined || gpuSubNumber <= subNumber);
    const ge = (serie, version, subNumber) => is(serie) && gpuVersion >= version
        && (subNumber === undefined || gpuSubNumber >= subNumber);
    const brand = (b) => b === gpuBrand;
    const methods = { is, le, ge, brand };

    let qualityName = 'low';
    for (let quality in testObject) {
        const testObjQuality = testObject[quality];
        for (let i = 0, l = testObjQuality.length; i < l; i++) {
            const args = [ ...testObjQuality[i] ];
            let firstArg = args.shift();
            if (firstArg === 'mobile' && !mobileGPU) continue;
            else if (firstArg === 'not-mobile' && mobileGPU) continue;
            if (!methods[firstArg]) firstArg = args.shift();
            const method = methods[firstArg];
            if (!method) continue;
            if (!method(...args)) continue;
            qualityName = quality;
            break;
        }
        if (qualityName !== 'low') break;
    }

    return gpuGetQualityObject(qualityName);
}


function gpuGetQualityObject(qualityStr) {
    const qualities = QUALITIES;
    const qualityIndex = Math.max(0, Object.values(qualities).indexOf(qualityStr));
    return { qualities, qualityIndex, detectedQualityIndex: qualityIndex };
}
